import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import React from 'react'
import lodash from 'src/utils/lodash'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger, NOOP_VOID } from 'wdc-cube'

import Badge, { badgeClasses } from '@mui/material/Badge'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import { type OnChangeFn, type PaginationState, type SortingState } from '@tanstack/react-table'
import {
    getIsRowSelected,
    MaterialReactTable,
    MRT_Column,
    MRT_TableInstance,
    MRT_Updater,
    MRT_VisibilityState,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_ColumnSizingState,
    type MRT_RowSelectionState
} from 'material-react-table'
import PopupState, { bindMenu, bindTrigger, type InjectedProps } from 'material-ui-popup-state'

import { LocaleProvider } from 'src/utils/views'

import DownloadIcon from '@mui/icons-material/Download'
import RestoreIcon from '@mui/icons-material/Restore'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined'
import StraightenIcon from '@mui/icons-material/Straighten'

import { CustomTableColumnFilter } from 'src/components/customtablecolumnfilter/views/custom_table_column_filter'
import { TableEmpresaScope, type CrmExportOption, type EmpresaRow } from '../tcm_scopes'
import {
    mrtBodyCellSx,
    mrtBodyRowSx,
    mrtBottomToolbarSx,
    mrtFooterCellSx,
    mrtHeadCellSx,
    mrtPaperSx,
    mrtTopToolbarSx
} from './helpers/tcm_mrt'
import { TcmColumnVisibilityButton } from './helpers/tcm_column_visibility_button'
import { formatNumberToCompactLongForm, pluralize } from './helpers/tcm_view_utils'
import { DateFns } from 'src/utils/date-fns'
import { parseLocalDateNoTZ } from 'src/utils/formatter-utils'
import ConfirmExportDataLimit from 'src/main/views/components/ConfirmExportDataLimit'
import { Caption, CaptionCount, SpanNumber } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-VIEW-LIST-PJ')
const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        width: 0,
        alignSelf: 'stretch'
    }
})

const CartBadge = styled(Badge)({
    [`& .${badgeClasses.badge}`]: {
        top: -12,
        right: -6,
        backgroundColor: 'var(--green-700)',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700
    }
})

const ActionsMenu = styled(Menu)({
    '& .MuiPaper-root': {
        minWidth: 208,
        padding: 7,
        borderRadius: 10,
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-2)',
        backgroundImage: 'none'
    },
    '& .MuiMenuItem-root': {
        gap: 9,
        padding: '9px 10px',
        borderRadius: 7,
        fontSize: 13.5,
        minHeight: 0,
        color: 'var(--ink)'
    },
    '& .MuiMenuItem-root:hover': {
        backgroundColor: '#f0f4f1'
    }
})

let GRID_COLUMN_DEFS: MRT_ColumnDef<EmpresaRow>[] = []

export type EmpresaTableViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: TableEmpresaScope
}

export class EmpresaTableViewClass implements FCClassContext<EmpresaTableViewProps> {
    // :: Fields
    scope!: TableEmpresaScope
    loaded = false
    loading = true
    mrtLocation = LocaleProvider.get().mrt
    classes!: ReturnType<typeof useStyles>['classes']
    data?: EmpresaRow[]
    dataVer = 0
    rowSelection: MRT_RowSelectionState = {}

    onSyncState(props: EmpresaTableViewProps) {
        initializeStatics()
        this.scope = props.scope
        this.loaded = this.scope.loaded
        this.loading = !this.loaded
        this.classes = useStyles().classes
    }

    render(props: EmpresaTableViewProps): React.JSX.Element {
        const { classes, scope, loading: isLoading, rowSelection } = this

        if (!this.data || this.dataVer !== scope.datasetVersion) {
            this.data = [...scope.dataset]
            this.dataVer = scope.datasetVersion

            for (const selKey of Object.keys(rowSelection)) {
                delete rowSelection[selKey]
            }

            for (const row of this.data) {
                if (row.selected) {
                    rowSelection[this.getRowId(row)] = true
                }
            }
        }

        const table = useMaterialReactTable({
            localization: this.mrtLocation,
            columns: GRID_COLUMN_DEFS,
            data: this.data,
            defaultDisplayColumn: { enableResizing: true },
            enableColumnResizing: true,
            enableFullScreenToggle: false,
            enableRowSelection: true,
            enableSelectAll: true,
            enableColumnOrdering: true,
            enableStickyHeader: true,
            enableGrouping: false,
            enableRowVirtualization: true,
            rowVirtualizerOptions: { overscan: 5 },
            enableRowNumbers: false,
            layoutMode: 'grid-no-grow',
            getRowId: this.getRowId,

            // Overall appearance
            muiTablePaperProps: {
                elevation: 0,
                className: clsx(classes.view, props.className),
                sx: mrtPaperSx
            },
            muiTopToolbarProps: { sx: mrtTopToolbarSx },
            muiBottomToolbarProps: { sx: mrtBottomToolbarSx },
            renderColumnActionsMenuItems: this.renderColumnActionsMenuItems,
            renderTopToolbarCustomActions: this.renderTopToolbarCustomActions,
            renderToolbarInternalActions: this.renderToolbarInternalActions,
            muiTableHeadCellProps: { sx: mrtHeadCellSx },
            muiTableBodyCellProps: { sx: mrtBodyCellSx },
            muiTableFooterCellProps: { sx: mrtFooterCellSx },
            // Filtering
            enableColumnActions: true,
            enableColumnFilters: true,
            columnFilterModeOptions: ['popover'],
            columnFilterDisplayMode: 'popover',
            manualFiltering: true,
            enableGlobalFilter: false,
            onColumnFiltersChange: this.emitColumnFilters,
            onGlobalFilterChange: this.emitGlobalFilter,
            onRowSelectionChange: this.emitRowSelectionChange,
            // pagging
            manualPagination: true,
            rowCount: scope.rowCount,
            onPaginationChange: this.emitPaginationChange,
            onSortingChange: this.emitSortingChange,
            muiPaginationProps: {
                rowsPerPageOptions: [100, 1000, 10_000],
                showRowsPerPage: true,
                showLastButton: false
            },
            // Content
            muiTableContainerProps: { sx: { maxHeight: '600px' } },
            muiTableBodyRowProps: (props) => ({
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                onClick: (_event) => {
                    const { row } = props
                    const checked = !getIsRowSelected(props)
                    row.toggleSelected(checked)
                },
                sx: mrtBodyRowSx,
                style: {
                    cursor: 'pointer',
                    userSelect: 'none'
                }
            }),
            muiSelectCheckboxProps: (props) => ({
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                onChange: (_event, checked) => {
                    const { row } = props
                    row.toggleSelected(checked)
                }
            }),
            initialState: {
                density: 'compact',
                columnVisibility: this.scope.columnVisibility,
                rowSelection
            },
            onColumnSizingChange: this.setColumnSizing,
            state: {
                isLoading,
                pagination: {
                    pageIndex: scope.pageIndex,
                    pageSize: scope.pageSize
                },
                sorting: this.scope.sorting,
                columnFilters: this.scope.filters,
                rowSelection,
                columnSizing: this.scope.columnSizing,
                columnVisibility: this.scope.columnVisibility
            },
            onColumnVisibilityChange: this.setColumnVisibility
        })

        const tableRec = table as unknown as Record<string, unknown>
        tableRec.$scope = scope

        return (
            <>
                <MaterialReactTable table={table} />
                <ConfirmExportDataLimit scope={this.scope.exportDataLimitScope} />
            </>
        )
    }

    getRowId = (originalRow: EmpresaRow) => {
        return originalRow.cnpj
    }

    retractAllColumns = (table: MRT_TableInstance<EmpresaRow>) => {
        const defaultSizing: Record<string, number> = {}
        table.getAllLeafColumns().forEach((col) => {
            if (col.getIsVisible()) {
                defaultSizing[col.id] = col.columnDef.size ?? 100
            }
        })
        table.setColumnSizing(defaultSizing)
    }
    expandAllColumns = (table: MRT_TableInstance<EmpresaRow>) => {
        const font = '14px Roboto, Helvetica, Arial, sans-serif'
        const padding = 32
        const newSizing: Record<string, number> = {}

        table.getAllLeafColumns().forEach((column) => {
            if (column.getIsVisible()) {
                const colId = column.id
                let maxWidth = getTextWidth(column.columnDef.header, font) + 40

                table.getPrePaginationRowModel().rows.forEach((row) => {
                    const cellWidth = getTextWidth(row.getValue(colId), font) + padding
                    if (cellWidth > maxWidth) {
                        maxWidth = cellWidth
                    }
                })

                newSizing[colId] = maxWidth
            }
        })

        table.setColumnSizing(newSizing)
    }

    renderToolbarInternalActions = ({ table }: { table: MRT_TableInstance<EmpresaRow> }) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    columnGap: 1
                }}
            >
                <TcmColumnVisibilityButton table={table} />

                <Tooltip title="Expandir todas as colunas">
                    <IconButton edge="start" color="inherit" onClick={() => this.expandAllColumns(table)}>
                        <StraightenIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Restaurar tamanho das colunas">
                    <IconButton edge="start" color="inherit" onClick={() => this.retractAllColumns(table)}>
                        <RestoreIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {this.scope.checkedRowCount > 0 && (
                    <ActionOverSelectedsButton
                        count={this.scope.checkedRowCount}
                        onCopyCnpjClick={this.emitCopyCnpjClick}
                        onOpenCorporateRelationsClick={this.emitOpenCorporateRelationsClick}
                        exportOptions={this.scope.crmExportOptions}
                        onCrmExportClick={this.emitCrmExportClick}
                    />
                )}
                {this.scope.downloadEnabled && (
                    <Tooltip title="Download dos dados">
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => {
                                const columns = table
                                    .getVisibleLeafColumns()
                                    .map((c) => c.id)
                                    .filter((c) => !c.startsWith('mrt-row'))
                                this.emitDownloadClick(columns)
                            }}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        )
    }

    renderColumnActionsMenuItems = ({
        closeMenu,
        column,
        table,
        internalColumnMenuItems
    }: {
        closeMenu: () => void
        column: MRT_Column<EmpresaRow>
        internalColumnMenuItems: React.ReactNode[]
        table: MRT_TableInstance<EmpresaRow>
    }): React.ReactNode[] => {
        const items: React.ReactNode[] = []

        const font = '14px Roboto, Helvetica, Arial, sans-serif'
        const padding = 32
        const tolerance = 2
        const getIdealColumnWidth = () => {
            const colId = column.id

            const headerText = typeof column.columnDef.header === 'string' ? column.columnDef.header : colId

            let maxWidth = getTextWidth(headerText, font) + 40

            table.getRowModel().rows.forEach((row) => {
                const width = getTextWidth(row.getValue(colId), font) + padding
                if (width > maxWidth) {
                    maxWidth = width
                }
            })

            return Math.ceil(maxWidth)
        }

        const idealWidth = getIdealColumnWidth()
        const currentWidth = column.getSize()
        const isAlreadyExpanded = currentWidth >= idealWidth - tolerance

        internalColumnMenuItems.forEach((item) => {
            items.push(item)

            if (
                React.isValidElement<{ label?: unknown }>(item) &&
                typeof item.props.label === 'string' &&
                item.props.label.includes('Restaurar tamanho da coluna')
            ) {
                items.push(
                    <MenuItem
                        key={`auto-fit-column-${column.id}`}
                        title="Ajusta a largura ao maior conteúdo visível"
                        disabled={isAlreadyExpanded}
                        onClick={() => {
                            table.setColumnSizing((prev) => ({
                                ...prev,
                                [column.id]: idealWidth
                            }))
                            closeMenu()
                        }}
                    >
                        <div style={{ minWidth: '36px', marginLeft: '1px' }}>
                            <StraightenIcon fontSize="small" />
                        </div>
                        Expandir esta coluna
                    </MenuItem>
                )
            }
        })

        return items
    }

    renderTopToolbarCustomActions = () => {
        return (
            <div>
                <Caption>{this.scope.caption}</Caption>
                <CaptionCount>
                    {pluralize(this.scope.rowCount, 'empresa encontrada', 'empresas encontradas')}
                </CaptionCount>
            </div>
        )
    }

    emitRowSelectionChange: OnChangeFn<MRT_RowSelectionState> = (stateUpdater) => {
        if (lodash.isFunction(stateUpdater)) {
            this.rowSelection = stateUpdater(this.rowSelection)
            this.scope.onSelectionChange(new Map(Object.entries(this.rowSelection))).catch(LOG.caught)
        }
    }

    emitColumnFilters: OnChangeFn<MRT_ColumnFiltersState> = (filterStateUpdater) => {
        if (lodash.isFunction(filterStateUpdater)) {
            const newFiler = filterStateUpdater(this.scope.filters)
            this.scope.onFiltrationChange(newFiler).catch(LOG.caught)
        }
    }

    emitGlobalFilter = () => {
        LOG.debug('emitGlobalFilter: Nao deveria estar ativo')
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    emitPaginationChange: OnChangeFn<PaginationState> = (pageStateUpdater) => {
        if (lodash.isFunction(pageStateUpdater)) {
            const newState = pageStateUpdater({
                pageIndex: this.scope.pageIndex,
                pageSize: this.scope.pageSize
            })

            this.scope.onPaginationChange(newState.pageIndex, newState.pageSize).catch(LOG.caught)
        }
    }

    emitSortingChange: OnChangeFn<SortingState> = (pageStateUpdater) => {
        if (lodash.isFunction(pageStateUpdater)) {
            const newSort = pageStateUpdater(this.scope.sorting)
            this.scope.onSortingChange(newSort)
        }
    }

    readonly emitDownloadClick = (columns: string[]) => this.scope.onDownloadClick(columns).catch(LOG.caught)

    readonly setColumnVisibility: OnChangeFn<MRT_VisibilityState> = (columnsVisibilityUpdater) => {
        if (lodash.isFunction(columnsVisibilityUpdater)) {
            const newVisibility = columnsVisibilityUpdater(this.scope.columnVisibility)
            this.scope.onColumnsVisibilityChange(newVisibility).catch(LOG.caught)
        } else {
            this.scope.onColumnsVisibilityChange(columnsVisibilityUpdater).catch(LOG.caught)
        }
    }

    readonly setColumnSizing = (columns: MRT_Updater<MRT_ColumnSizingState>) => {
        if (typeof columns === 'function') {
            const current = this.scope.columnSizing || {}
            const newSizing = columns(current)
            this.scope.setColumnSizing(newSizing).catch(LOG.caught)
        } else {
            this.scope.setColumnSizing(columns).catch(LOG.caught)
        }
    }

    readonly emitCopyCnpjClick = () => this.scope.onCopyCnpj().catch(LOG.caught)
    readonly emitOpenCorporateRelationsClick = () => this.scope.onOpenCorporateRelations().catch(LOG.caught)
    readonly emitCrmExportClick = (connectionId: number) => this.scope.onCrmExport(connectionId).catch(LOG.caught)
}

export default classToFComponent(EmpresaTableViewClass, React)

let initializeStatics = () => {
    GRID_COLUMN_DEFS = static_newGridColumnDefs()
    initializeStatics = NOOP_VOID
}

function static_newGridColumnDefs(): MRT_ColumnDef<EmpresaRow>[] {
    return [
        {
            enableColumnFilter: false,
            accessorKey: 'cnpj',
            header: 'CNPJ',
            size: 120,
            enableHiding: false,
            Footer: () => <span>TOTAL:</span>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'cnes',
            header: 'CNES',
            size: 100
        },
        {
            enableColumnFilter: false,
            accessorKey: 'nome',
            header: 'Nome',
            size: 240
        },
        {
            enableColumnFilter: false,
            enableSorting: false,
            accessorKey: 'tipo_estabelecimento_saude',
            header: 'Tipo de Estabelecimento de Saúde',
            size: 200
        },
        {
            enableColumnFilter: false,
            accessorKey: 'natureza_juridica',
            header: 'Natureza Jurídica',
            size: 140
        },
        {
            enableColumnFilter: false,
            accessorKey: 'natureza_juridica_grp',
            header: 'Natureza Jurídica - Pública/Privada',
            size: 140,
            filterVariant: 'checkbox',
            Cell: ({ cell }) => {
                const val = cell.getValue<number | null | undefined>()
                return <span>{val === 1 ? 'Privada' : 'Pública'}</span>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'situacao_cadastral_nme',
            header: 'Situação Cadastral',
            size: 140
        },
        {
            enableColumnFilter: false,
            accessorKey: 'dta_situacao_cadastral',
            header: 'Data Situação Cadastral',
            size: 140,
            filterVariant: 'date-range',
            Cell: ({ cell }) => {
                const val = cell.getValue<Date | null | undefined>()
                if (!val) {
                    return <span></span>
                }

                return (
                    <SpanNumber>{DateFns.formatDate(parseLocalDateNoTZ(val.toISOString()), 'dd/MM/yyyy')}</SpanNumber>
                )
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'dta_inicio_atividade',
            header: 'Início Atividade',
            size: 140,
            filterVariant: 'date-range',
            Cell: ({ cell }) => {
                const val = cell.getValue<Date | null | undefined>()
                if (!val) {
                    return <span></span>
                }

                return (
                    <SpanNumber>{DateFns.formatDate(parseLocalDateNoTZ(val.toISOString()), 'dd/MM/yyyy')}</SpanNumber>
                )
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'cnae',
            header: 'CNAE',
            size: 100
        },
        {
            enableColumnFilter: false,
            accessorKey: 'cnae_descricao',
            header: 'Descrição CNAE',
            size: 240
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_cep',
            header: 'CEP',
            size: 130
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_rua_nome',
            header: 'Logradouro',
            size: 130
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_numero',
            header: 'Número',
            size: 100
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_complemento',
            header: 'Complemento',
            size: 100
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_bairro_nome',
            header: 'Bairro',
            size: 130
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_cidade_nome',
            header: 'Município',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_uf',
            header: 'UF',
            size: 50
        },
        {
            enableColumnFilter: false,
            accessorKey: 'regiao_metropolitana',
            header: 'Região Metropolitana',
            size: 180
        },
        {
            enableColumnFilter: false,
            accessorKey: 'regiao_geo_imediata',
            header: 'Região Geográfica Imediata',
            size: 180
        },
        {
            enableColumnFilter: false,
            accessorKey: 'regiao_geo_intermediaria',
            header: 'Região Geográfica Intermediária',
            size: 180
        },
        {
            enableColumnFilter: false,
            accessorKey: 'socios_nomes',
            header: 'Sócios',
            size: 200,
            Cell: ({ cell }) => {
                const nomes = cell.getValue<string>()
                return (
                    <Tooltip title={nomes}>
                        <span>{nomes}</span>
                    </Tooltip>
                )
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'emp_captial_social',
            header: 'Capital Social',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'emp_idade_anos',
            header: 'Idade Emp. Anos',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{cell.getValue<number>()}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'emp_qtd_grupo',
            header: 'Qtde Emp. Grupo',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd',
            header: 'Funcionários',
            size: 120,
            Cell: ({ cell }) => <span>{formatNumberToCompactLongForm(cell.getValue<number>())}</span>,
            Footer: ({ table }) => {
                const scope = (table as unknown as { $scope: TableEmpresaScope }).$scope
                const value = formatNumberToCompactLongForm(scope.total.func_qtd)
                return <SpanNumber>{value}</SpanNumber>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_masc',
            header: 'Func. Masculinos',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_fem',
            header: 'Func. Femininos',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_0_a_18',
            header: 'Func. de 0 a 18',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_19_a_33',
            header: 'Func. de 19 a 33',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_34_a_48',
            header: 'Func. de 34 a 48',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_49_a_58',
            header: 'Func. de 49 a 58',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_59_ou_mais',
            header: 'Func. de 59 ou mais',
            size: 120
        },
        {
            enableColumnFilter: false,
            accessorKey: 'faturamento_estimado',
            header: 'Faturamento',
            size: 120,
            Cell: ({ cell }) => <span>{formatNumberToCompactLongForm(cell.getValue<number>())}</span>,
            Footer: ({ table }) => {
                const scope = (table as unknown as { $scope: TableEmpresaScope }).$scope
                const value = formatNumberToCompactLongForm(scope.total.faturamento_estimado)
                return <SpanNumber>{value}</SpanNumber>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'faturamento_estimado_grupo',
            header: 'Fat. Grupo',
            size: 120,
            Cell: ({ cell }) => <span>{formatNumberToCompactLongForm(cell.getValue<number>())}</span>,
            Footer: ({ table }) => {
                const scope = (table as unknown as { $scope: TableEmpresaScope }).$scope
                const value = formatNumberToCompactLongForm(scope.total.faturamento_estimado_grupo)
                return <SpanNumber>{value}</SpanNumber>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'matriz_cnpj',
            header: 'CNPJ Matriz',
            size: 140
        },
        {
            enableColumnFilter: false,
            accessorKey: 'matriz_nome',
            header: 'Nome Matriz',
            size: 250
        },
        {
            enableColumnFilter: false,
            accessorKey: 'matriz_uf',
            header: 'UF Matriz',
            size: 50
        },
        {
            enableColumnFilter: false,
            accessorKey: 'grupo_cnpj',
            header: 'CNPJ Grupo Emp.',
            size: 140
        },
        {
            enableColumnFilter: true,
            accessorKey: 'telefone1',
            header: 'Telefone 1 - RF',
            Filter: ({ column }) => <CustomTableColumnFilter column={column} />,
            size: 80
        },
        {
            enableColumnFilter: true,
            accessorKey: 'telefone2',
            header: 'Telefone 2 - RF',
            Filter: ({ column }) => <CustomTableColumnFilter column={column} />,
            size: 80
        },
        {
            enableColumnFilter: true,
            accessorKey: 'email',
            header: 'E-mail',
            Filter: ({ column }) => <CustomTableColumnFilter column={column} />,
            size: 150
        },
        {
            enableColumnFilter: false,
            accessorKey: 'salario_total',
            header: 'Salário Total',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'salario_total_grupo',
            header: 'Salário Total Grupo',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'func_qtd_matriz_e_filiais',
            header: 'Total de Funcionários - Matriz + Filiais',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'qtd_filiais',
            header: 'Total de Filiais',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'web_nome',
            header: 'Internet - Nome',
            size: 150
        },
        {
            enableColumnFilter: false,
            accessorKey: 'web_categoria',
            header: 'Internet - Categorias',
            size: 80
        },
        {
            enableColumnFilter: false,
            accessorKey: 'web_nota',
            header: 'Internet - Nota',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'web_qtd_avaliacoes',
            header: 'Internet - Total de avaliações',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: true,
            accessorKey: 'web_site',
            header: 'Internet - Website',
            Filter: ({ column }) => <CustomTableColumnFilter column={column} />,
            size: 150
        },
        {
            enableColumnFilter: true,
            accessorKey: 'web_tel',
            header: 'Internet - Telefone',
            Filter: ({ column }) => <CustomTableColumnFilter column={column} />,
            size: 80
        },
        {
            enableColumnFilter: false,
            accessorKey: 'web_tempo_medio',
            header: 'Internet - Tempo Médio',
            size: 120,
            Cell: ({ cell }) => <SpanNumber>{formatNumberToCompactLongForm(cell.getValue<number>())}</SpanNumber>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_lat',
            header: 'Internet Latitude',
            size: 50
        },
        {
            enableColumnFilter: false,
            accessorKey: 'endereco_lon',
            header: 'Internet Longitude',
            size: 50
        },
        {
            enableColumnFilter: false,
            accessorKey: 'optante_simples',
            header: 'Optante Simples',
            size: 80,
            filterVariant: 'checkbox',
            Cell: ({ cell }) => {
                let val = ''
                switch (cell.getValue<string>() ?? '') {
                    case 'S':
                        val = 'SIM'
                        break
                    case 'N':
                        val = 'NÃO'
                        break
                    default:
                        val = ''
                }
                return <span>{val}</span>
            }
        },
        {
            enableColumnFilter: false,
            accessorKey: 'porte_codigo',
            header: 'Porte da Empresa - RF',
            size: 80,
            Cell: ({ cell }) => <span>{formatPorteReceita(cell.getValue<number>())}</span>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'porte_funcionarios',
            header: 'Porte por Funcionários',
            size: 100,
            Cell: ({ cell }) => <span>{formatPorteEmpresa(cell.getValue<number>())}</span>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'porte_fat_cnpj',
            header: 'Porte por Fat. Estimado do CNPJ',
            size: 120,
            Cell: ({ cell }) => <span>{formatPorteEmpresa(cell.getValue<number>())}</span>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'porte_fat_grupo',
            header: 'Porte por Fat. Estimado do Grupo Econômico',
            size: 140,
            Cell: ({ cell }) => <span>{formatPorteEmpresa(cell.getValue<number>())}</span>
        },
        {
            enableColumnFilter: false,
            enableSorting: false,
            accessorKey: 'porte_leitos',
            header: 'Porte do Estabelecimento de Saúde (Leitos)',
            size: 160,
            Cell: ({ cell }) => <span>{formatPorteLeitos(cell.getValue<string>())}</span>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'regime_tributario',
            header: 'Regime Tributário',
            size: 160,
            Cell: ({ cell }) => <span>{formatRegimeTributario(cell.getValue<string | number>())}</span>
        },
        {
            enableColumnFilter: false,
            accessorKey: 'score_risco',
            header: 'Score Risco',
            size: 80,
            Cell: ({ cell }) => {
                const val = cell.getValue<string>()
                return <span>{val === '?' ? 'Sem score' : val}</span>
            }
        },
        {
            enableColumnFilter: false,
            enableSorting: false,
            accessorKey: 'cadeia_valor_saude',
            header: 'Cadeia de Valor da Saúde',
            size: 100,
            Cell: ({ cell }) => <span>{cell.getValue<number>() === 1 ? 'SIM' : 'NÃO'}</span>
        }
    ]
}

// Mesmos rótulos do filtro Regime Tributário (tcm_presenter_filter_pane.ts).
function formatRegimeTributario(codigo: string | number | null | undefined) {
    switch (`${codigo ?? ''}`) {
        case '1':
            return 'Lucro Arbitrado'
        case '2':
            return 'Lucro Real'
        case '3':
            return 'Lucro Real/Arbitrado'
        case '4':
            return 'Lucro Presumido/Real'
        case '5':
            return 'Lucro Presumido/Real/Arbitrado'
        case '6':
            return 'Simples Nacional/MEI'
        case '7':
            return 'Lucro Presumido/Arbitrado'
        case '8':
            return 'Lucro Presumido'
        case '9':
            return 'Isenta do IRPJ'
        case '10':
            return 'Imune do IRPJ'
        case '11':
            return 'Simples Nacional'
        case '12':
            return 'MEI (excluído do Simples)'
        default:
            return ''
    }
}

// Domínio da Receita: 01 Micro, 03 Pequena, 05 Demais -- e nada além disso.
// O 5 vinha como 'Média' e rotulava errado 16,8 milhões de empresas; os casos
// 6 e 9 não ocorrem em nenhum documento do índice.
function formatPorteReceita(porte: number | null | undefined) {
    switch (porte) {
        case 1:
            return 'Micro'
        case 3:
            return 'Pequena'
        case 5:
            return 'Demais'
        default:
            return ''
    }
}

// Porte derivado de funcionários ou faturamento: aqui Média e Grande existem.
function formatPorteEmpresa(porte: number | null | undefined) {
    switch (porte) {
        case 1:
            return 'Micro'
        case 3:
            return 'Pequena'
        case 5:
            return 'Média'
        case 6:
            return 'Grande'
        case 9:
            return 'Demais'
        default:
            return ''
    }
}

function formatPorteLeitos(porte: string | null | undefined) {
    switch (porte) {
        case 'SL':
            return 'ESTABELECIMENTO SEM LEITO'
        case 'P':
            return 'PEQUENO PORTE'
        case 'M':
            return 'MÉDIO PORTE'
        case 'G':
            return 'GRANDE PORTE'
        case 'EX':
            return 'PORTE ESPECIAL OU EXTRA'
        default:
            return ''
    }
}

type ActionOverSelectedsButtonProps = {
    className?: string
    count: number
    onCopyCnpjClick?: () => void
    onOpenCorporateRelationsClick?: () => void
    exportOptions?: CrmExportOption[]
    onCrmExportClick?: (connectionId: number) => void
}

class ActionOverSelectedsButtonClass implements FCClassContext<ActionOverSelectedsButtonProps> {
    popupState!: InjectedProps
    onCopyCnpjClick?: ActionOverSelectedsButtonProps['onCopyCnpjClick']
    onOpenCorporateRelationsClick?: ActionOverSelectedsButtonProps['onOpenCorporateRelationsClick']
    onCrmExportClick?: ActionOverSelectedsButtonProps['onCrmExportClick']
    exportOptions: CrmExportOption[] = []
    count = 0

    onSyncState(props: ActionOverSelectedsButtonProps) {
        this.onCopyCnpjClick = props.onCopyCnpjClick
        this.onOpenCorporateRelationsClick = props.onOpenCorporateRelationsClick
        this.onCrmExportClick = props.onCrmExportClick
        this.exportOptions = props.exportOptions ?? []
        this.count = props.count
    }

    readonly renderExportOption = (opt: CrmExportOption) => (
        <MenuItem key={opt.connectionId} disabled={this.count <= 0} onClick={this.emitCrmExportClick(opt.connectionId)}>
            {`Exportar para ${opt.name}`}
        </MenuItem>
    )

    render(props: ActionOverSelectedsButtonProps): React.JSX.Element {
        const { count } = props

        return (
            <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                    (this.popupState = popupState),
                    (
                        <React.Fragment>
                            <IconButton edge="start" color="inherit" {...bindTrigger(popupState)}>
                                <ShoppingCartIcon fontSize="small" />
                                <CartBadge badgeContent={count} overlap="circular" />
                            </IconButton>
                            <ActionsMenu {...bindMenu(popupState)}>
                                {this.emitOnCopuCnpjClick && (
                                    <MenuItem onClick={this.emitOnCopuCnpjClick}>
                                        CNPJ(s) para área de transferência
                                    </MenuItem>
                                )}
                                {this.onOpenCorporateRelationsClick && (
                                    <MenuItem onClick={this.emitOpenCorporateRelationsClick}>
                                        Abrir relações societárias
                                    </MenuItem>
                                )}
                                {this.exportOptions.map(this.renderExportOption)}
                            </ActionsMenu>
                        </React.Fragment>
                    )
                )}
            </PopupState>
        )
    }

    emitOnCopuCnpjClick = () => {
        this.popupState && this.popupState.close()
        this.onCopyCnpjClick && this.onCopyCnpjClick()
    }

    emitOpenCorporateRelationsClick = () => {
        this.popupState && this.popupState.close()
        this.onOpenCorporateRelationsClick && this.onOpenCorporateRelationsClick()
    }

    readonly emitCrmExportClick = (connectionId: number) => () => {
        this.popupState && this.popupState.close()
        this.onCrmExportClick && this.onCrmExportClick(connectionId)
    }
}

const ActionOverSelectedsButton = classToFComponent(ActionOverSelectedsButtonClass, React)

const getTextWidth = (value: unknown, font: string) => {
    const text = value instanceof Date ? DateFns.formatDate(value, 'dd/MM/yyyy HH:mm:ss') : String(value ?? '')
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (context) {
        context.font = font
        return context.measureText(text).width
    }
    return 0
}
